import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Heart, MessageCircle, Share2, Eye, Play, Clock, BarChart3,
  TrendingUp, ExternalLink, AlertTriangle, RefreshCw, MousePointerClick,
  ThumbsDown, Trash2,
} from 'lucide-react';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import {
  facebookPageService,
  FbPageItem,
  FbPagePostInsightsResponse,
  sumReactions,
} from '@/services/facebook-page.service';

interface Props {
  open: boolean;
  onClose: () => void;
  post: FbPageItem | null;
  /** Called after a successful delete so the parent page can refetch. */
  onDeleted?: (postId: string) => void;
}

const METRIC_META: Record<string, { icon: React.ReactNode; label: string; isDuration?: boolean; tone?: 'default' | 'success' | 'info' | 'warning' | 'danger' }> = {
  post_impressions:                       { icon: <BarChart3 size={14} />,        label: 'Impressions',        tone: 'info' },
  post_impressions_unique:                { icon: <Eye size={14} />,              label: 'Reach',              tone: 'info' },
  post_engaged_users:                     { icon: <TrendingUp size={14} />,       label: 'Engaged users',      tone: 'success' },
  post_clicks:                            { icon: <MousePointerClick size={14} />, label: 'Clicks' },
  post_negative_feedback:                 { icon: <ThumbsDown size={14} />,       label: 'Negative feedback',  tone: 'warning' },
  post_video_views:                       { icon: <Play size={14} />,             label: 'Video views' },
  post_video_view_time:                   { icon: <Clock size={14} />,            label: 'Total watch time',   isDuration: true },
  post_video_avg_time_watched:            { icon: <Clock size={14} />,            label: 'Avg watch time',     isDuration: true },
  post_video_complete_views_organic:      { icon: <Play size={14} />,             label: 'Complete views' },
  post_reactions_like_total:              { icon: <Heart size={14} />,            label: 'Like' },
  post_reactions_love_total:              { icon: <Heart size={14} />,            label: 'Love' },
  post_reactions_wow_total:               { icon: <Heart size={14} />,            label: 'Wow' },
  post_reactions_haha_total:              { icon: <Heart size={14} />,            label: 'Haha' },
  post_reactions_sorry_total:             { icon: <Heart size={14} />,            label: 'Sorry' },
  post_reactions_anger_total:             { icon: <Heart size={14} />,            label: 'Anger' },
};

function formatDuration(ms: number): string {
  const totalSec = Math.round(ms / 1000);
  const m = Math.floor(totalSec / 60);
  const s = totalSec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

export const FbPagePostInsightsModal: React.FC<Props> = ({ open, onClose, post, onDeleted }) => {
  const [data, setData] = useState<FbPagePostInsightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = async (postId: string) => {
    setLoading(true);
    setError(null);
    try {
      const r = await facebookPageService.postInsights(postId);
      setData(r);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load insights');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!open || !post?.id) return;
    setData(null);
    load(post.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, post?.id]);

  const remove = async () => {
    if (!post?.id) return;
    if (!window.confirm('Permanently delete this Facebook Page post? Meta does not provide a restore.')) return;
    setDeleting(true);
    try {
      await facebookPageService.deletePost(post.id);
      toast.success('Post deleted');
      onDeleted?.(post.id);
      onClose();
    } catch {
      // axios interceptor toasts.
    } finally { setDeleting(false); }
  };

  if (!post) return null;
  const dateStr = post.created_time ? new Date(post.created_time).toLocaleString() : '—';

  // Group metrics: engagement basics first, then video, then reactions breakdown.
  const reactionMetrics = (data?.metrics ?? []).filter((m) => m.name.startsWith('post_reactions_'));
  const otherMetrics = (data?.metrics ?? []).filter((m) => !m.name.startsWith('post_reactions_'));
  const totalReactions = data ? sumReactions(data.metrics) : (post.reactions?.summary?.total_count ?? 0);

  return (
    <Modal open={open} onClose={onClose} title="Post insights" widthClass="max-w-3xl">
      <div className="space-y-4">
        {/* Header */}
        <div className="flex gap-3 rounded-xl border border-border bg-surface p-3">
          <div className="h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-surface-alt">
            {post.full_picture && <img src={post.full_picture} alt="" className="h-full w-full object-cover" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="rounded-full bg-info-soft px-2 py-0.5 text-2xs font-semibold text-info">
                {post.status_type ?? 'POST'}
              </span>
              <span className="text-2xs text-text-muted">{dateStr}</span>
            </div>
            {post.message && (
              <p className="line-clamp-3 text-xs text-text-secondary leading-relaxed">{post.message}</p>
            )}
            {post.permalink_url && (
              <a
                href={post.permalink_url}
                target="_blank"
                rel="noreferrer"
                className="mt-1.5 inline-flex items-center gap-1 text-2xs text-primary hover:underline"
              >
                Open on Facebook <ExternalLink size={10} />
              </a>
            )}
          </div>
        </div>

        {/* Engagement strip */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Tile icon={<Heart size={14} />} label="Reactions" value={totalReactions.toLocaleString()} />
          <Tile icon={<MessageCircle size={14} />} label="Comments" value={(post.comments?.summary?.total_count ?? 0).toLocaleString()} />
          <Tile icon={<Share2 size={14} />} label="Shares" value={(post.shares?.count ?? 0).toLocaleString()} />
          {data && otherMetrics.find((m) => m.name === 'post_impressions_unique') && (
            <Tile
              icon={<Eye size={14} />}
              label="Reach"
              value={otherMetrics.find((m) => m.name === 'post_impressions_unique')!.value.toLocaleString()}
              tone="info"
            />
          )}
        </div>

        {/* Full metric panel */}
        <div className="rounded-xl border border-border bg-surface">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-border">
            <div className="flex items-center gap-2">
              <BarChart3 size={13} className="text-primary" />
              <span className="text-xs font-semibold text-text-primary">All metrics from Meta</span>
            </div>
            <button
              type="button"
              onClick={() => post.id && load(post.id)}
              disabled={loading}
              className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-2 py-0.5 text-2xs text-text-secondary hover:bg-surface-alt disabled:opacity-50"
            >
              <RefreshCw size={10} className={clsx(loading && 'animate-spin')} /> Refresh
            </button>
          </div>

          <div className="p-3 space-y-3">
            {loading && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {[0, 1, 2, 3, 4, 5].map((i) => <div key={i} className="skeleton h-16 rounded-lg" />)}
              </div>
            )}

            {!loading && error && (
              <div className="flex items-start gap-2 rounded-lg border border-danger/40 bg-danger-soft p-3 text-xs text-danger">
                <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                <div>
                  <div className="font-semibold">Couldn't load insights</div>
                  <div className="mt-0.5 text-text-secondary">{error}</div>
                </div>
              </div>
            )}

            {!loading && !error && data && data.metrics.length === 0 && (
              <div className="rounded-lg border border-dashed border-border bg-surface-alt/40 px-4 py-6 text-center text-xs text-text-muted">
                No insights available yet — Meta needs ~24h after posting before metrics populate.
              </div>
            )}

            {!loading && !error && otherMetrics.length > 0 && (
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {otherMetrics.map((m) => {
                  const meta = METRIC_META[m.name];
                  const display = meta?.isDuration ? formatDuration(m.value) : m.value.toLocaleString();
                  return (
                    <Tile
                      key={m.name}
                      icon={meta?.icon ?? <BarChart3 size={14} />}
                      label={meta?.label ?? m.title ?? m.name}
                      value={display}
                      tone={meta?.tone}
                      title={m.description}
                    />
                  );
                })}
              </div>
            )}

            {!loading && !error && reactionMetrics.length > 0 && (
              <div>
                <div className="mb-1.5 text-2xs uppercase tracking-wide text-text-muted">Reaction breakdown</div>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                  {reactionMetrics.map((m) => {
                    const meta = METRIC_META[m.name];
                    return (
                      <Tile
                        key={m.name}
                        icon={meta?.icon ?? <Heart size={14} />}
                        label={meta?.label ?? m.name.replace('post_reactions_', '').replace('_total', '')}
                        value={m.value.toLocaleString()}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 border-t border-border px-1 pt-4 mt-4">
        <span className="text-2xs text-text-muted">
          {data ? `Pulled from Meta at ${new Date(data.fetchedAt).toLocaleTimeString()}` : ' '}
        </span>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={remove}
            loading={deleting}
            iconLeft={<Trash2 size={13} />}
            className="text-danger hover:bg-danger-soft"
          >
            Delete post
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose} disabled={deleting}>Close</Button>
        </div>
      </div>
    </Modal>
  );
};

const Tile: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: 'default' | 'success' | 'info' | 'warning' | 'danger';
  title?: string;
}> = ({ icon, label, value, tone, title }) => (
  <div title={title} className="rounded-xl border border-border bg-surface-alt/40 p-2.5">
    <div className={clsx(
      'flex items-center gap-1.5 mb-0.5 text-2xs uppercase tracking-wide',
      tone === 'success' ? 'text-success'
      : tone === 'info' ? 'text-info'
      : tone === 'warning' ? 'text-warning'
      : tone === 'danger' ? 'text-danger'
      : 'text-text-muted',
    )}>
      <span>{icon}</span>
      <span className="truncate">{label}</span>
    </div>
    <div className="font-display text-lg font-bold text-text-primary tabular-nums">{value}</div>
  </div>
);
